import React, { useState, useEffect } from 'react';
import { FolderPlus, X, Edit2, Trash2, ArrowLeft, Folder, Plus } from 'lucide-react';
import './CreateFolder.css'; 
import { baseurl } from '../../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';

const FolderManager = ({ onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('list'); 
  const [folders, setFolders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form State
  const [currentFolder, setCurrentFolder] = useState({ 
    id: null, 
    name: '', 
    description: '', 
    color: 'Purple', 
    projectIds: [] 
  });

  const getAuthHeader = () => ({ 
    headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` } 
  });

  const toggleModal = () => {
    const nextState = !isOpen;
    setIsOpen(nextState);
    if (nextState) {
      setView('list');
      fetchFolders();
      fetchProjects(); 
    }
  };

  const fetchFolders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}getUserFolders`, getAuthHeader());
      if (res.data.success) setFolders(res.data.data);
    } catch (err) {
      console.error("Fetch folders error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${baseurl}getUserProjects`, getAuthHeader());
      if (res.data.success) setProjects(res.data.data);
    } catch (err) {
      console.error("Fetch projects error:", err);
    }
  };

  const openCreateView = () => {
    setCurrentFolder({ id: null, name: '', description: '', color: 'Purple', projectIds: [] });
    setView('create');
  };

  const openEditView = (folder) => {
    // Initial project IDs come from the nested "projects" array in your API response
    const existingProjectIds = folder.projects ? folder.projects.map(p => p.id) : [];

    setCurrentFolder({ 
      id: folder.id, 
      name: folder.name, 
      description: folder.description || '', 
      color: folder.color, 
      projectIds: existingProjectIds 
    });
    setView('edit');
  };

  const handleSave = async () => {
    if (!currentFolder.name.trim()) return Swal.fire('Error', 'Name is required', 'error');
    
    setLoading(true);
    try {
      const endpoint = view === 'create' ? 'createFolder' : 'updateFolder';
      const res = await axios.post(`${baseurl}${endpoint}`, currentFolder, getAuthHeader());
      
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Saved', timer: 1000, showConfirmButton: false });
        setView('list');
        fetchFolders();
        fetchProjects(); 
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      Swal.fire('Error', 'Operation failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleProjectSelection = (projectId) => {
    setCurrentFolder(prev => ({
      ...prev,
      projectIds: prev.projectIds.includes(projectId)
        ? prev.projectIds.filter(id => id !== projectId)
        : [...prev.projectIds, projectId]
    }));
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Folder?',
      text: "Projects inside will be moved to 'Uncategorized'.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        const res = await axios.delete(`${baseurl}deleteFolder/${id}`, getAuthHeader());
        if (res.data.success) {
          fetchFolders();
          fetchProjects();
          if (onRefresh) onRefresh();
        }
      } catch (err) {
        Swal.fire('Error', 'Failed to delete folder.', 'error');
      }
    }
  };

  return (
    <>
      <button className="nav-tab create-folder-btn-custom" onClick={toggleModal}>
        <FolderPlus size={16} /> Manage Folders
      </button>

      {isOpen && (
        <div className="cm-modal-overlay">
          <div className="cm-modal-container large">
            <div className="cm-modal-header">
              <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {view !== 'list' && (
                  <ArrowLeft className="back-btn" onClick={() => setView('list')} size={20} />
                )}
                <h2>
                  {view === 'list' ? 'Folder Management' : 
                   view === 'create' ? 'Create New Folder' : 'Edit Folder Settings'}
                </h2>
              </div>
              <button className="cm-close-x" onClick={toggleModal}><X size={20} /></button>
            </div>

            <div className="cm-modal-body">
              {view === 'list' ? (
                <div className="folder-management-view">
                  <button className="add-inline-btn" onClick={openCreateView}>
                    <Plus size={16} /> Create New Folder
                  </button>
                  
                  <div className="folder-grid">
                    {folders.map(f => (
                      <div key={f.id} className="folder-item-card" style={{ borderLeft: `4px solid ${f.color}` }}>
                        <div className="folder-info">
                          <a href={`/projects?i=${f.id}&n=${f.name}`} target="_blank" className="folder-info cskla">
                          <Folder size={20} style={{ color: f.color }} />
                          <div>
                            <h3>{f.name}</h3>
                            <span>{f.project_count || 0} Projects</span>
                          </div>
                          </a>
                        </div>
                        <div className="folder-actions">
                          <button onClick={() => openEditView(f)} title="Edit"><Edit2 size={14} /></button>
                          <button onClick={() => handleDelete(f.id)} className="del-btn" title="Delete"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                    {folders.length === 0 && !loading && <p className="empty-msg">No folders found. Create one to get started!</p>}
                  </div>
                </div>
              ) : (
                <div className="folder-form">
                  <div className="cm-form-group">
                    <label>Folder Name</label>
                    <input 
                      type="text" className="cm-input" 
                      placeholder="e.g. Residential Projects"
                      value={currentFolder.name} 
                      onChange={(e) => setCurrentFolder({...currentFolder, name: e.target.value})} 
                    />
                  </div>

                  <div className="cm-form-group">
                    <label>Theme Color</label>
                    <select 
                      className="cm-select" 
                      value={currentFolder.color} 
                      onChange={(e) => setCurrentFolder({...currentFolder, color: e.target.value})}
                    >
                      <option value="Purple">Purple</option>
                      <option value="Blue">Blue</option>
                      <option value="Green">Green</option>
                      <option value="Red">Red</option>
                      <option value="Orange">Orange</option>
                    </select>
                  </div>

                  <div className="cm-form-group">
                    <label>Add/Remove Projects</label>
                    <p className="cm-help-text">Select any project below to add it to this folder. Projects already in this folder are highlighted.</p>
                    <div className="fm-project-selection-list">
                      {projects.map(p => {
                        const isSelectedInForm = currentFolder.projectIds.includes(p.id);
                        
                        return (
                          <div 
                            key={p.id} 
                            className={`fm-sel-item ${isSelectedInForm ? 'active' : ''}`}
                            onClick={() => toggleProjectSelection(p.id)}
                          >
                            <div className="fm-sel-item-content">
                              <span className="fm-project-id">{p.property_id || `ID: ${p.id}`}</span>
                              {p.folder_id && !isSelectedInForm && (
                                <span className="fm-already-in"> (In another folder)</span>
                              )}
                            </div>
                            {isSelectedInForm ? <X size={14} /> : <Plus size={14} />}
                          </div>
                        );
                      })}
                      {projects.length === 0 && <p className="empty-msg">No projects available to add.</p>}
                    </div>
                  </div>

                  <div className="cm-modal-footer">
                    <button className="cm-btn-cancel" onClick={() => setView('list')}>Cancel</button>
                    <button className="cm-btn-submit" onClick={handleSave} disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FolderManager;